import { type ReactElement } from "react";
import { Modal } from "@/components/ui/Modal";
import type { BrokerAccount } from "@/lib/brokerAccounts";

interface Props {
  account: BrokerAccount | null;
  busy: boolean;
  onCancel: () => void;
  onConfirmDeferred: () => void;
  onConfirmNow: () => void;
}

/** Three-choice disconnect modal:
 *   - "Disconnect after trade closes" (recommended; needs backend, stubbed for now)
 *   - "Disconnect now" (immediate, works today)
 *   - "Cancel" (close modal)
 *
 *  When the account has no open trades, the page-level handler skips this
 *  modal entirely and calls onConfirmNow directly.
 */
export function DisconnectModal({
  account,
  busy,
  onCancel,
  onConfirmDeferred,
  onConfirmNow,
}: Readonly<Props>): ReactElement {
  const open = account !== null;
  const openTrade = account?.openPositionCount ?? 0;
  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={`Disconnect from ${account?.broker ?? "broker"}?`}
      description={`${String(openTrade)} open trade${openTrade === 1 ? "" : "s"} running. Pick how to handle ${openTrade === 1 ? "it" : "them"}.`}
      icon="ti-plug-off"
      iconTone="warning"
      size="lg"
      footer={
        <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      }
    >
      <div className="flex flex-col gap-2">
        <ChoiceCard
          tone="accent"
          icon="ti-clock-check"
          title="Disconnect after trade closes"
          subtitle="Recommended. Trailing SL keeps running. Signs out automatically when the trade exits."
          badge="Coming soon"
          onClick={onConfirmDeferred}
          disabled={busy}
        />
        <ChoiceCard
          tone="danger"
          icon="ti-alert-triangle"
          title="Disconnect now"
          subtitle="Open trade stays at broker but bot stops trailing SL. You'll need to exit manually."
          onClick={onConfirmNow}
          disabled={busy}
        />
      </div>
    </Modal>
  );
}

interface ChoiceCardProps {
  tone: "accent" | "danger";
  icon: string;
  title: string;
  subtitle: string;
  badge?: string;
  onClick: () => void;
  disabled: boolean;
}

function ChoiceCard({
  tone,
  icon,
  title,
  subtitle,
  badge,
  onClick,
  disabled,
}: Readonly<ChoiceCardProps>): ReactElement {
  const wrapBg =
    tone === "accent"
      ? "border-2 border-accent-border bg-accent-bg"
      : "border border-line bg-surface-1";
  const iconCls = tone === "accent" ? "text-accent" : "text-danger";
  const titleCls = tone === "accent" ? "text-accent" : "text-danger";
  return (
    <button
      type="button"
      className={`flex items-start gap-3 rounded-ctl p-3 text-left transition-colors hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60 ${wrapBg}`}
      onClick={onClick}
      disabled={disabled}
    >
      <i className={`ti ${icon} mt-0.5 text-xl ${iconCls}`} />
      <div className="flex-1">
        <div className={`flex items-center gap-2 text-sm font-medium ${titleCls}`}>
          {title}
          {badge !== undefined && <span className="pill bg-surface-2 text-ink-muted">{badge}</span>}
        </div>
        <div className="mt-0.5 text-xs text-ink-secondary">{subtitle}</div>
      </div>
    </button>
  );
}
